import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import * as Dialog from "@radix-ui/react-dialog";
import { Plus, Trash2 } from "lucide-react";

import { AssessmentsDocument, ChoiceSet } from "./assessmentsTypes";
import styles from "./CompanyAssessments.module.css";
import {
    addChoice,
    deleteChoice,
    deleteChoiceSet,
    updateChoice,
    updateChoiceSet
} from "./assessmentsMutations";
import { MAX_TITLE_LENGTH } from "./assessmentsSchema";

interface ChoiceSetEditorModalProps {
    open: boolean;
    choiceSet: ChoiceSet | null;
    document: AssessmentsDocument;
    onOpenChange: (open: boolean) => void;
    onDraftChange: (document: AssessmentsDocument) => void;
}

export function ChoiceSetEditorModal({
    open,
    choiceSet,
    document,
    onOpenChange,
    onDraftChange
}: ChoiceSetEditorModalProps) {
    const { t } = useTranslation();
    const [titleDraft, setTitleDraft] = useState("");

    const currentChoiceSet = choiceSet
        ? document.choice_sets.find(item => item.id === choiceSet.id) ?? null
        : null;

    useEffect(() => {
        if (open && currentChoiceSet) {
            setTitleDraft(currentChoiceSet.title ?? "");
        }
    }, [open, currentChoiceSet?.id, currentChoiceSet?.title]);

    if (!currentChoiceSet) {
        return null;
    }

    const handleTitleBlur = () => {
        onDraftChange(updateChoiceSet(document, currentChoiceSet.id, { title: titleDraft }));
    };

    const handleAddChoice = () => {
        const { document: next } = addChoice(document, currentChoiceSet.id);
        onDraftChange(next);
    };

    const handleDeleteChoiceSet = () => {
        try {
            onDraftChange(deleteChoiceSet(document, currentChoiceSet.id));
            onOpenChange(false);
        } catch (error) {
            alert(error instanceof Error ? error.message : String(error));
        }
    };

    return (
        <Dialog.Root open={open} onOpenChange={onOpenChange}>
            <Dialog.Portal>
                <Dialog.Overlay className={styles.dialogOverlay} />
                <Dialog.Content className={styles.dialogContent}>
                    <Dialog.Title className={styles.dialogTitle}>
                        {t("settingsPage.company.assessments.editChoiceSet")}
                    </Dialog.Title>

                    <label className={styles.dialogFieldLabel}>
                        {t("settingsPage.company.assessments.choiceSetTitle")}
                        <input
                            className={styles.dialogInput}
                            type="text"
                            value={titleDraft}
                            maxLength={MAX_TITLE_LENGTH}
                            onChange={event => setTitleDraft(event.target.value)}
                            onBlur={handleTitleBlur}
                        />
                    </label>

                    {currentChoiceSet.choices.map(choice => (
                        <div key={choice.id} className={styles.choiceRow}>
                            <input
                                className={styles.dialogInput}
                                type="text"
                                value={choice.label}
                                onChange={event =>
                                    onDraftChange(
                                        updateChoice(
                                            document,
                                            currentChoiceSet.id,
                                            choice.id,
                                            event.target.value
                                        )
                                    )
                                }
                            />
                            <button
                                type="button"
                                className={styles.dialogDeleteButton}
                                onClick={() => {
                                    try {
                                        onDraftChange(
                                            deleteChoice(document, currentChoiceSet.id, choice.id)
                                        );
                                    } catch (error) {
                                        alert(error instanceof Error ? error.message : String(error));
                                    }
                                }}
                                aria-label={t("settingsPage.company.assessments.deleteChoice")}
                            >
                                <Trash2 size={14} />
                            </button>
                        </div>
                    ))}

                    <button type="button" className={styles.dialogAddButton} onClick={handleAddChoice}>
                        <Plus size={14} />
                        {t("settingsPage.company.assessments.addChoice")}
                    </button>

                    <div className={styles.dialogActions}>
                        <button
                            type="button"
                            className={styles.dialogDeleteButton}
                            onClick={handleDeleteChoiceSet}
                        >
                            {t("settingsPage.company.assessments.deleteChoiceSet")}
                        </button>
                        <div className={styles.dialogPrimaryActions}>
                            <Dialog.Close asChild>
                                <button type="button" className={styles.dialogCancelButton}>
                                    {t("settingsPage.account.cancel")}
                                </button>
                            </Dialog.Close>
                        </div>
                    </div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
}
